<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const store = useStore();
const getters = useStoreGetters();

onMounted(() => {
  store.dispatch('conversationFieldDefinitions/fetchAll');
});

const allFields = computed(() => getters['conversationFieldDefinitions/getAll'].value);
const uiFlags   = computed(() => getters['conversationFieldDefinitions/uiFlags'].value);

const searchQuery = ref('');
const expandedId  = ref(null);
const draggingIdx = ref(null);
const dragOverIdx = ref(null);

const filteredFields = computed(() =>
  allFields.value.filter(f =>
    f.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
);

const FIELD_TYPES = [
  { id: 'text',        label: 'Single line text', icon: 'i-lucide-type' },
  { id: 'textarea',    label: 'Multi line text',  icon: 'i-lucide-align-left' },
  { id: 'number',      label: 'Number',           icon: 'i-lucide-hash' },
  { id: 'decimal',     label: 'Decimal',          icon: 'i-lucide-calculator' },
  { id: 'date',        label: 'Date',             icon: 'i-lucide-calendar' },
  { id: 'list',        label: 'Dropdown',         icon: 'i-lucide-list' },
  { id: 'checkbox',    label: 'Checkbox',         icon: 'i-lucide-check-square' },
  { id: 'multiselect', label: 'Multiselect',      icon: 'i-lucide-list-checks' },
];
const TYPE_ICON  = Object.fromEntries(FIELD_TYPES.map(t => [t.id, t.icon]));
const TYPE_LABEL = Object.fromEntries(FIELD_TYPES.map(t => [t.id, t.label]));

// ── Dialog ─────────────────────────────────────────────────────────────────────
const dialogRef    = ref(null);
const editingField = ref(null);
const deleting     = ref(null);

const emptyForm = () => ({
  label:     '',
  type:      'text',
  options:   [],
  newOption: '',
});
const form = ref(emptyForm());

const openAdd = () => {
  editingField.value = null;
  form.value = emptyForm();
  dialogRef.value?.open();
};

const openEdit = field => {
  editingField.value = field;
  form.value = {
    label:     field.label,
    type:      field.field_type,
    options:   [...(field.options || [])],
    newOption: '',
  };
  dialogRef.value?.open();
};

const closePanel = () => { dialogRef.value?.close(); editingField.value = null; };

const addOption = () => {
  const v = form.value.newOption.trim();
  if (!v) return;
  if (form.value.options.some(o => (typeof o === 'string' ? o : o.label) === v)) {
    useAlert('Choice already exists');
    return;
  }
  if (form.value.options.length >= 300) { useAlert('Maximum 300 choices allowed'); return; }
  form.value.options.push({ label: v, value: v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') });
  form.value.newOption = '';
};

const removeOption = idx => form.value.options.splice(idx, 1);

const hasChoices = computed(() => ['list', 'multiselect'].includes(form.value.type));

const buildPayload = () => ({
  label:      form.value.label.trim(),
  field_type: form.value.type,
  options:    hasChoices.value ? form.value.options : [],
  enabled:    true,
});

const saveField = async () => {
  if (!form.value.label.trim()) return;
  try {
    if (editingField.value) {
      await store.dispatch('conversationFieldDefinitions/update', { id: editingField.value.id, ...buildPayload() });
      useAlert('Field updated');
    } else {
      await store.dispatch('conversationFieldDefinitions/create', buildPayload());
      useAlert('Field created');
    }
    closePanel();
  } catch (e) {
    useAlert(e?.message || 'Failed to save field');
  }
};

const deleteField = async field => {
  if (!confirm(`Delete "${field.label}"? It will be removed from all conversations.`)) return;
  deleting.value = field.id;
  try {
    await store.dispatch('conversationFieldDefinitions/delete', field.id);
    useAlert('Field deleted');
    if (expandedId.value === field.id) expandedId.value = null;
  } catch {
    useAlert('Failed to delete field');
  } finally {
    deleting.value = null;
  }
};

// ── Toggle enabled ─────────────────────────────────────────────────────────────
const toggleEnabled = async field => {
  try {
    await store.dispatch('conversationFieldDefinitions/update', {
      id:         field.id,
      label:      field.label,
      field_type: field.field_type,
      options:    field.options || [],
      enabled:    !field.enabled,
    });
  } catch { useAlert('Failed to update field'); }
};

// ── Drag & drop ────────────────────────────────────────────────────────────────
const onDragStart = (e, idx) => {
  draggingIdx.value = idx;
  e.dataTransfer.effectAllowed = 'move';
};

const onDragOver = (e, idx) => {
  e.preventDefault();
  dragOverIdx.value = idx;
};

const onDragLeave = () => { dragOverIdx.value = null; };

const onDrop = async (e, toIdx) => {
  e.preventDefault();
  const fromIdx = draggingIdx.value;
  draggingIdx.value = null;
  dragOverIdx.value = null;
  if (fromIdx === null || fromIdx === toIdx) return;

  const fields = [...allFields.value];
  const [moved] = fields.splice(fromIdx, 1);
  fields.splice(toIdx, 0, moved);
  store.commit('conversationFieldDefinitions/SET_RECORDS', fields);
  await store.dispatch('conversationFieldDefinitions/reorder', fields.map(f => f.id));
};

const onDragEnd = () => { draggingIdx.value = null; dragOverIdx.value = null; };
</script>

<template>
  <div class="mx-auto max-w-4xl px-6 py-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">Conversation Properties</h1>
        <p class="text-sm text-n-slate-9 mt-0.5">
          Define custom fields that appear inside every conversation's properties panel.
        </p>
      </div>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
        @click="openAdd"
      >
        <span class="i-lucide-plus size-3.5" />
        Add field
      </button>
    </div>

    <!-- Search -->
    <div class="mb-4">
      <div class="relative">
        <span class="i-lucide-search absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-9" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search fields..."
          class="w-full pl-9 pr-3 py-2 text-sm border border-n-weak rounded-lg bg-transparent text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500 dark:bg-n-alpha-2"
        />
      </div>
    </div>

    <!-- Loading -->
    <div v-if="uiFlags.isFetching" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 animate-spin size-6 text-woot-500" />
    </div>

    <div v-else>
      <!-- Field rows -->
      <div class="space-y-1.5">
        <div
          v-for="(field, idx) in filteredFields"
          :key="field.id"
          :draggable="!searchQuery"
          class="rounded-xl border transition-all"
          :class="[
            dragOverIdx === idx ? 'border-woot-400 bg-woot-50/30 dark:bg-woot-900/10' : 'border-n-weak bg-white dark:bg-n-solid-1',
            draggingIdx === idx ? 'opacity-50' : 'opacity-100',
            !field.enabled ? 'opacity-60' : '',
          ]"
          @dragstart="onDragStart($event, idx)"
          @dragover="onDragOver($event, idx)"
          @dragleave="onDragLeave"
          @drop="onDrop($event, idx)"
          @dragend="onDragEnd"
        >
          <!-- Main row -->
          <div class="flex items-center gap-3 px-3 py-3">
            <!-- Drag handle -->
            <span
              class="i-lucide-grip-vertical size-4 text-n-slate-6 cursor-grab active:cursor-grabbing shrink-0"
              :class="searchQuery ? 'opacity-0 pointer-events-none' : ''"
            />

            <!-- Type icon -->
            <span :class="TYPE_ICON[field.field_type] || 'i-lucide-type'" class="size-4 text-n-slate-9 shrink-0" />

            <!-- Field name -->
            <span class="flex-1 text-sm font-medium text-n-blue-11 min-w-0 truncate">{{ field.label }}</span>

            <!-- Type badge -->
            <span class="text-xs px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-9 shrink-0">
              {{ TYPE_LABEL[field.field_type] || field.field_type }}
            </span>

            <!-- Expand chevron -->
            <button
              class="p-1 rounded hover:bg-n-alpha-2 transition-colors text-n-slate-9"
              @click="expandedId = expandedId === field.id ? null : field.id"
            >
              <span
                class="i-lucide-chevron-down size-4 transition-transform duration-200"
                :class="expandedId === field.id ? 'rotate-180' : ''"
              />
            </button>
          </div>

          <!-- Expanded actions -->
          <div
            v-if="expandedId === field.id"
            class="flex items-center gap-2 px-4 pb-3 pt-0 border-t border-n-weak/50"
          >
            <span class="text-xs text-n-slate-8 font-mono mr-2">{{ field.field_key }}</span>
            <div class="flex-1" />
            <button
              class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
              @click="openEdit(field)"
            >
              <span class="i-lucide-pencil size-3" />
              Edit field
            </button>
            <button
              class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-n-slate-11 border border-n-weak rounded-lg hover:bg-n-alpha-2 transition-colors"
              @click="toggleEnabled(field)"
            >
              <span :class="field.enabled ? 'i-lucide-eye-off' : 'i-lucide-eye'" class="size-3" />
              {{ field.enabled ? 'Hide field' : 'Show field' }}
            </button>
            <button
              class="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-red-500 border border-red-200 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              :disabled="deleting === field.id"
              @click="deleteField(field)"
            >
              <span :class="deleting === field.id ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-trash-2'" class="size-3" />
              Delete field
            </button>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!filteredFields.length" class="flex flex-col items-center justify-center py-16 text-center">
        <span class="i-lucide-message-square-text size-10 text-n-slate-6 mb-3" />
        <p class="text-sm font-medium text-n-slate-9">
          {{ searchQuery ? 'No fields match your search' : 'No conversation properties yet' }}
        </p>
        <p v-if="!searchQuery" class="text-xs text-n-slate-8 mt-1">
          Fields you add here appear in the Conversation Properties panel.
        </p>
        <button
          v-if="!searchQuery"
          class="mt-4 flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-woot-600 border border-woot-300 rounded-lg hover:bg-woot-50 dark:hover:bg-woot-900/20 transition-colors"
          @click="openAdd"
        >
          <span class="i-lucide-plus size-3.5" /> Add your first field
        </button>
      </div>
    </div>

    <!-- Add / Edit dialog -->
    <Dialog
      ref="dialogRef"
      :title="editingField ? 'Edit Conversation Property' : 'Add Conversation Property'"
      :confirm-button-label="uiFlags.isSaving ? 'Saving…' : (editingField ? 'Update' : 'Create Field')"
      cancel-button-label="Cancel"
      :disable-confirm-button="!form.label.trim() || uiFlags.isSaving"
      :is-loading="uiFlags.isSaving"
      @confirm="saveField"
      @close="closePanel"
    >
      <div class="flex flex-col gap-4">
        <!-- Label -->
        <div>
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Field Label *</label>
          <input
            v-model="form.label"
            type="text"
            placeholder="e.g. Issue Category, Resolution Time…"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            @keydown.enter.prevent="saveField"
          />
        </div>

        <!-- Field Type -->
        <div>
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">Field Type</label>
          <div class="grid grid-cols-4 gap-2">
            <button
              v-for="ft in FIELD_TYPES"
              :key="ft.id"
              class="flex items-center gap-1.5 px-2 py-2 text-xs rounded-lg border transition-colors"
              :class="form.type === ft.id
                ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 text-woot-700 dark:text-woot-300'
                : 'border-n-weak text-n-slate-11 hover:border-woot-300 disabled:opacity-40 disabled:cursor-not-allowed'"
              :disabled="!!editingField"
              @click="!editingField && (form.type = ft.id)"
            >
              <span :class="ft.icon" class="size-3.5 shrink-0" />
              {{ ft.label }}
            </button>
          </div>
          <p v-if="editingField" class="mt-1 text-xs text-n-slate-9">Field type cannot be changed after creation.</p>
        </div>

        <!-- Choices (for list / multiselect) -->
        <div v-if="hasChoices">
          <label class="block text-xs font-semibold text-n-slate-11 uppercase tracking-wide mb-1.5">
            Choices
            <span class="normal-case font-normal text-n-slate-8 ml-1">({{ form.options.length }}/300)</span>
          </label>
          <div class="flex flex-wrap gap-1.5 mb-2 min-h-[1.5rem]">
            <span
              v-for="(opt, idx) in form.options"
              :key="idx"
              class="flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-n-alpha-2 text-n-slate-12"
            >
              {{ typeof opt === 'string' ? opt : opt.label }}
              <button class="hover:text-red-500 text-n-slate-9 ml-0.5" @click="removeOption(idx)">×</button>
            </span>
            <span v-if="!form.options.length" class="text-xs text-n-slate-9 italic">No choices yet</span>
          </div>
          <div class="flex gap-2">
            <input
              v-model="form.newOption"
              type="text"
              placeholder="Add a choice…"
              class="flex-1 px-2 py-1.5 text-xs rounded-lg border border-n-weak dark:bg-n-alpha-2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500"
              @keydown.enter.prevent="addOption"
            />
            <button
              class="px-3 py-1.5 text-xs font-medium text-woot-600 border border-woot-200 rounded-lg hover:bg-woot-50 dark:hover:bg-woot-900/20"
              @click="addOption"
            >+ Add</button>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
